import React from 'react';
import { Combobox } from './Combobox.component';
import { IBrandSearchQuery, IBrandOption, useBrandNames, useBrandSearch } from '../../../services/brand';

// #region BrandComboboxProps
export interface BrandComboboxProps {
    /**
     * Selected brand id, or null for "no brand / all brands". A string is allowed
     * so a synthetic pinned row (see `pinnedOptions`) can be the current value.
     */
    value: string | number | null;
    onChange: (value: number | null) => void;
    /** Trigger label when nothing is selected. */
    placeholder?: string;
    searchPlaceholder?: string;
    /** Render the leading "null" entry (i.e. an "All brands" option). */
    nullable?: boolean;
    nullLabel?: string;
    disabled?: boolean;
    className?: string;
    triggerClassName?: string;
    ariaLabel?: string;
    /** Pass 'all' to include inactive brands, or `includeUnnamed` for stub rows. */
    active?: IBrandSearchQuery['active'];
    includeUnnamed?: boolean;
    /** Only list brands that have at least one order (Orders page filter). */
    hasOrders?: boolean;
    /**
     * Rows pinned above the searched brands (e.g. the Orders bar's synthetic
     * "All except Wall Blush" entry). Their ids may be strings.
     */
    pinnedOptions?: Array<{ id: string | number; name: string }>;
    /** Called instead of `onChange` when a pinned row is picked. */
    onPinnedSelect?: (id: string | number) => void;
    /** Label override for the trigger, used when a pinned row is the current value. */
    selectedLabelOverride?: string;
}

type Row = { id: string | number; name: string };

// #region BrandCombobox
/**
 * The brand picker every surface should use.
 *
 * Every keystroke queries the database (`GET /brands/search`) rather than
 * filtering a pre-fetched array, which is what previously made brands outside the
 * first fetched page — and every inactive or unnamed brand — impossible to find.
 * The trigger label comes from the id→name cache, never from the current result
 * page, because a selected brand is usually not in it.
 */
export const BrandCombobox: React.FunctionComponent<BrandComboboxProps> = ({
    value,
    onChange,
    placeholder = 'All Brands',
    searchPlaceholder = 'Search brands...',
    nullable = false,
    nullLabel,
    disabled = false,
    className,
    triggerClassName,
    ariaLabel = 'Filter by brand',
    active,
    includeUnnamed,
    hasOrders,
    pinnedOptions,
    onPinnedSelect,
    selectedLabelOverride,
}): React.ReactElement => {
    const { brands, loading, query, setQuery, loadMore, hasMore } = useBrandSearch({ active, includeUnnamed, hasOrders });

    // Hydrate the selected brand's name independently of the result page.
    const selectedId = typeof value === 'number' ? value : null;
    const { nameOf } = useBrandNames(React.useMemo(() => (selectedId == null ? [] : [selectedId]), [selectedId]));

    const options = React.useMemo<Row[]>(() => {
        const searched = brands.map((brand: IBrandOption) => ({ id: brand.id, name: brand.name }));
        // Pinned rows are filtered locally: they are synthetic, not database rows.
        const term = query.trim().toLowerCase();
        const pinned = (pinnedOptions ?? []).filter((row) => !term || row.name.toLowerCase().includes(term));
        return [...pinned, ...searched];
    }, [brands, pinnedOptions, query]);

    const handleChange = (next: string | number | null) => {
        if (next === null) return onChange(null);
        if (pinnedOptions?.some((row) => String(row.id) === String(next))) {
            return onPinnedSelect ? onPinnedSelect(next) : undefined;
        }
        onChange(Number(next));
    };

    // A pinned row supplies its own label; a real brand's comes from the cache.
    const pinnedLabel = pinnedOptions?.find((row) => String(row.id) === String(value))?.name;
    const selectedLabel = selectedLabelOverride
        ?? pinnedLabel
        ?? (selectedId == null ? undefined : nameOf(selectedId) ?? `#${selectedId}`);

    return (
        <Combobox<Row>
            value={value}
            onChange={handleChange}
            options={options}
            labelKey="name"
            valueKey="id"
            placeholder={placeholder}
            searchPlaceholder={searchPlaceholder}
            nullable={nullable}
            nullLabel={nullLabel}
            disabled={disabled}
            className={className}
            triggerClassName={triggerClassName}
            ariaLabel={ariaLabel}
            onSearchChange={setQuery}
            searchValue={query}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={loadMore}
            selectedLabel={selectedLabel}
            emptyLabel="No brands found"
        />
    );
};
